<template>
	<Dialog :visible="true" style="width: 60vw" modal :closable="false">
		<template #header>
			<div class="flex justify-between w-full">
				<PageLabel :helpPageName="'MaterialAddBySearch'"
					><h1>{{ props.selectedMaterial !== null && props.selectedMaterial !== undefined ? "Modify" : "Add" }} Material</h1>
				</PageLabel>
				<Button label="Cancel" @click="$emit('cancel')" right />
			</div>
		</template>
		<h4 v-html="modMessage" class="mb-4"></h4>
		<icDataTable
			:columns="columns"
			:loading="loading"
			:table-data="filteredMaterialList"
			v-model:filters="filters"
			:row-class="(data: RawMaterialSelectionRow) => (isRowSelectable(data) ? '' : 'text-gray-400 bg-gray-100 cursor-not-allowed')"
			@row-click="rowClick"
			table-name="MaterialList"
			ref="dataTableComponent"
			:customProps="datatableProps"
			:persist-keyword-search="false"
		>
			<template #additionalFilters>
				<IcFormField>
					<div class="flex items-center gap-2">
						<MultiSelect :options="materialCategories" v-model="selectedCategories" :option-label="'name'" placeholder="Categories" class="w-64" />
						<i v-tooltip.right="categoryTooltip" class="pi pi-info-circle text-gray-500 cursor-help"></i>
					</div>
				</IcFormField>
			</template>
			<template #additionalButtons>
				<IcFormField>
					<div class="flex items-center">
						Database
						<ToggleSwitch inputId="showProxiesCheckbox" class="ml-4 mr-4" v-model="showProxiesCheckbox" :binary="true" />
						Proxy
					</div>
				</IcFormField>
			</template>
		</icDataTable>
	</Dialog>
	<!-- <Toast position="top-right" /> -->
</template>
<script setup lang="ts">
import { ref, onMounted, computed, watch, nextTick } from "vue"
import { ApiClient, RawMaterialSelectionRow, TreeItem } from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import Dialog from "primevue/dialog"
import ToggleSwitch from "primevue/toggleswitch"
import PageLabel from "./pageLabel.vue"
import Button from "primevue/button"
import * as Session from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"
import MultiSelect from "primevue/multiselect"
//icDataTable
import icDataTable from "./icComponents/icDataTable.vue"
import { ColumnProps } from "primevue/column"
import { DataTableFilterMeta, DataTableRowClickEvent } from "primevue/datatable"

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const saveAttempted = ref(false)

let toast = useToast()

const datatableProps = {
	rows: 5,
	selectionMode: "single",
}

const props = defineProps({
	assessmentId: { type: String, required: true },
	screenId: { type: String, required: true },
	selectableMaterials: { type: Array<RawMaterialSelectionRow>, required: true },
	selectedMaterial: { type: RawMaterialSelectionRow || null || undefined, required: false },
	stepId: { type: String, required: true },
	stepNumber: { type: Number, required: true },
})

const emit = defineEmits<{
	(e: "cancel"): void
	(e: "selected", selectedMaterial: RawMaterialSelectionRow): void
	(e: "deleted"): void
}>()
const filters = ref({} as DataTableFilterMeta)
const materialList = ref([] as RawMaterialSelectionRow[])
const selectedCategories = ref([] as { id: string; name: string }[])
const showProxiesCheckbox = ref(false)
const columns: Array<
	ColumnProps & {
		template?: (props: { data: any; field: string }) => any
		style?: string | object
	}
> = [
	{ field: "categoryName", header: "Category", filterField: "categoryName" },
	{ field: "materialName", header: "Material", filterField: "materialName" },
	{ field: "cas", header: "CAS", filterField: "cas" },
]
const materialCategories = computed(() => {
	var categories = new Array<{ id: string; name?: string }>()
	for (const material of materialList.value) {
		if (material.materialCategoryId && !categories.some((c) => c.id === material.materialCategoryId)) {
			categories.push({ id: material.materialCategoryId, name: material.categoryName })
		}
	}
	return categories.sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0))
})
const filteredMaterialList = computed(() =>
	materialList.value
		.filter((item) => (showProxiesCheckbox.value ? item.isProxy : !item.isProxy))
		.filter((item) => item.intermediateId !== props.stepId)
		.filter((item) => selectedCategories.value.length === 0 || selectedCategories.value.some((c) => c.id === item.materialCategoryId))
)
const categoryTooltip = computed(() => {
	if (!selectedCategories.value?.length) return "No categories selected"
	return "Selected categories: " + selectedCategories.value.map((c) => c.name).join(", ")
})
const isRowSelectable = (rowData: RawMaterialSelectionRow) => !(rowData.isIntermediate && rowData.stepNumber && rowData.stepNumber > props.stepNumber)
// import { h } from "vue"

// const columns: Array<
// 	ColumnProps & {
// 		template?: (props: { data: any; field: string }) => any
// 		style?: string | object
// 	}
// > = [
// 	{
// 		field: "categoryName",
// 		header: "Category",
// 		filterField: "categoryName",
// 	},
// 	{
// 		field: "materialName",
// 		header: "Material",
// 		filterField: "materialName",
// 	},
// 	{
// 		field: "cas",
// 		header: "CAS",
// 		filterField: "cas",
// 		template: (props) => {
// 			const value = props.data[props.field]
// 			const showIcon = value === "123-45-6"

// 			return h("div", { class: "flex items-center gap-2" }, [
// 				h("span", value),
// 				showIcon && h("i", { class: "pi pi-exclamation-triangle text-yellow-500" }),
// 			])
// 		},
// 	},
// ]

const loading = ref(false)
const dataTableComponent = ref(null)
let modMessage: String = ""
onMounted(() => {
	materialList.value = props.selectableMaterials
	if (props.selectedMaterial !== null && props.selectedMaterial !== undefined) {
		modMessage = "You are editing <b>" + props.selectedMaterial.materialName + "</b> material."
	}
})
const rowClick = (e: DataTableRowClickEvent) => {
	//console.log("SE", e.data)
	//Check if the row is selectable before emitting the selected event
	if (isRowSelectable(e.data)) emit("selected", e.data)
}

watch(showProxiesCheckbox, async (newValue) => {
	if (newValue) {
		materialList.value.sort((a, b) => a.climateChange - b.climateChange)
	}
})
</script>
<style scoped>
.button-container {
	margin: 10px;
}
.p-invalid {
	border-color: red !important;
}
.p-datatable-wrapper {
	max-height: 240px; /* 48px * 5 righe */
	overflow-y: auto;
}
</style>
